import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import {
  User,
  type AddOrgMemberRequest,
  type UpdateOrgMemberRequest,
  type AddOrgMemberResponse,
  type UpdateOrgMemberResponse,
  type DeleteOrgMemberResponse,
  type OrgMemberListItem,
  type UserStatus
} from '@bliks-io/contracts';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartments, useDepartmentTombstoneLookup } from '@/api/queries/useDepartments';
import { useOrganizationSeatUsage } from '@/api/queries/useSeatUsage';
import {
  useCreateOrgMember,
  useUpdateOrgMember,
  useDeleteOrgMember
} from '@/api/mutations/useOrgMembers';
import { useOrgMemberStatusActions } from '@/pages/organization/org-members/useOrgMemberStatusActions';
import { useCrudDialogState } from '@/hooks/useCrudDialogState';
import { useCrudHandlers } from '@/hooks/useCrudHandlers';
import {
  DeleteConfirmDialog,
  Badge,
  Button,
  Page,
  PageHeader,
  PageContent,
  UserCard,
  toUserCardProps,
  DataTable,
  Skeleton,
  createActionsColumn,
  createColumnHeader,
  cn,
  appendDeletedSuffix,
  isDeletedInLookup,
  DELETED_ENTITY_CLASS,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { HomeOrgDeletedBadge } from '@/components/home-org-deleted-badge/HomeOrgDeletedBadge';
import { getMemberStatusActions } from './memberStatusActions';
import { OrgMemberFormDialog } from './components/OrgMemberFormDialog';
import { InviteAllInactiveButton } from './components/InviteAllInactiveButton';
import { useAuthStore } from '@/stores/auth-store';
import { OrganizationTabsNav } from '@/components/organization/OrganizationTabsNav';
import { ContentHeader } from '@/components/content-header';
import { SeatUsageSummary } from '@/components/seat-usage/SeatUsageSummary';
import {
  getUserStatusLabel,
  getUserStatusColorScheme,
  getUserStatusOptions
} from '@/lib/user-utils';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { distinctValues } from '@/components/form/rhf';

export function OrgMembersListPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.orgMembers');
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const { data: orgMembers, isLoading } = useOrgMembers({
    classification: 'internal',
    withHomeOrgStatus: true
  });
  const { data: departments, isLoading: departmentsLoading } = useDepartments();
  // SeatUsageSummary mounts only with the table toolbar, after the member list
  // lands; reading here starts the request with the page. Unread, so it must
  // not re-render the page on every seat-usage change.
  useOrganizationSeatUsage({ notifyOnChangeProps: [] });
  const deptLookup = useDepartmentTombstoneLookup();

  // Mutations
  const createMutation = useCreateOrgMember();
  const updateMutation = useUpdateOrgMember();
  const deleteMutation = useDeleteOrgMember();

  // CRUD dialog state management
  const {
    formDialogOpen,
    editingEntity: editingMember,
    deleteDialogOpen,
    deletingEntityId: deletingMemberId,
    openCreate: handleCreate,
    openEdit: handleEdit,
    openDelete: handleDelete,
    closeFormDialog,
    closeFormDialogFrom,
    formDialogOpening,
    closeDeleteDialog
  } = useCrudDialogState<User>();

  // CRUD handlers with error handling
  const { handleFormSubmit, handleDeleteConfirm, isDeleting } = useCrudHandlers<
    User,
    AddOrgMemberRequest,
    UpdateOrgMemberRequest,
    AddOrgMemberResponse,
    UpdateOrgMemberResponse,
    DeleteOrgMemberResponse
  >({
    createMutation,
    updateMutation,
    deleteMutation,
    editingEntity: editingMember,
    deletingEntityId: deletingMemberId,
    t,
    onFormSuccess: closeFormDialog,
    onDeleteSuccess: closeDeleteDialog
  });

  // View handler
  const handleView = useCallback(
    (member: User) => {
      navigate({
        to: '/$slug/org-members/$orgMemberId',
        params: { slug, orgMemberId: member._id }
      });
    },
    [navigate, slug]
  );

  // Helper function to check if user is a department manager
  const isDepartmentManager = useCallback(
    (userId: string): boolean => {
      if (!departments) return false;
      return departments.some((d) => d.manager?._id === userId);
    },
    [departments]
  );

  const {
    handleSendInvitation,
    handleSuspend,
    handleUnsuspend,
    pendingMemberIds,
    memberDialogProps
  } = useOrgMemberStatusActions({
    dialogMemberId: editingMember?._id,
    dialogOpening: formDialogOpening,
    onDialogActionDone: closeFormDialogFrom
  });

  const locationFilterOptions = useMemo(
    () =>
      distinctValues(orgMembers?.map((member) => member.location)).map((location) => ({
        label: location,
        value: location
      })),
    [orgMembers]
  );

  // Define columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<OrgMemberListItem>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        meta: { label: t('common.Name'), filterType: 'text' },
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => (
          <Link
            to="/$slug/org-members/$orgMemberId"
            params={{ slug, orgMemberId: row.original._id }}
          >
            <UserCard
              user={toUserCardProps(row.original)}
              variant="name-only"
              // The table has its own status column — never mark twice.
              showLifecycleSuffix={false}
            />
          </Link>
        ),
        enableColumnFilter: true,
        size: 280
      },
      {
        accessorKey: 'email',
        meta: { label: t('common.Email'), filterType: 'text' },
        header: createColumnHeader(t('common.Email')),
        cell: ({ row }) => row.getValue('email'),
        enableColumnFilter: true
      },
      {
        id: 'department',
        // Plain name for sorting/filtering — the "(deleted)" suffix is presentation-only
        accessorFn: (row) => row.department?.name || '-',
        meta: { label: t('common.entityTypes.Department'), filterType: 'text' },
        header: createColumnHeader(t('common.entityTypes.Department')),
        cell: ({ row }) => {
          const dept = row.original.department;
          if (!dept) return '-';
          const isDeleted = isDeletedInLookup(dept._id, deptLookup);
          return (
            <span className={cn(isDeleted && DELETED_ENTITY_CLASS)}>
              {appendDeletedSuffix(dept.name, isDeleted, t)}
            </span>
          );
        },
        enableColumnFilter: true
      },
      {
        id: 'isDepartmentManager',
        accessorFn: (row) => isDepartmentManager(row._id),
        meta: {
          label: t('common.DepartmentManager'),
          filterType: 'select',
          filterOptions: [
            { label: t('common.Yes'), value: 'true' },
            { label: t('common.No'), value: 'false' }
          ]
        },
        header: createColumnHeader(t('common.DepartmentManager')),
        cell: ({ row }) => {
          if (departmentsLoading) return <Skeleton className="h-5 w-10" />;
          const isManager = isDepartmentManager(row.original._id);
          return (
            <Badge colorScheme={isManager ? 'success' : 'neutral'}>
              {isManager ? t('common.Yes') : t('common.No')}
            </Badge>
          );
        },
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          return String(row.getValue(columnId)) === filterValue;
        }
      },
      {
        id: 'mainRole',
        accessorFn: (row) => row.mainRole?.name || '-',
        meta: { label: t('organization.members.mainRole'), filterType: 'text' },
        header: createColumnHeader(t('organization.members.mainRole')),
        cell: ({ row }) => row.original.mainRole?.name || '-',
        enableColumnFilter: true
      },
      {
        id: 'location',
        accessorFn: (row) => row.location || '-',
        meta: {
          label: t('organization.members.location'),
          filterType: 'select',
          filterOptions: locationFilterOptions
        },
        header: createColumnHeader(t('organization.members.location')),
        cell: ({ row }) => row.original.location || '-',
        enableColumnFilter: true
      },
      {
        accessorKey: 'status',
        meta: {
          label: t('common.Status'),
          filterType: 'select',
          filterOptions: getUserStatusOptions(t)
        },
        header: createColumnHeader(t('common.Status')),
        cell: ({ row }) => {
          const status = row.getValue('status') as UserStatus;
          return (
            <div className="flex flex-wrap items-center gap-1">
              <Badge colorScheme={getUserStatusColorScheme(status)}>
                {getUserStatusLabel(status, t)}
              </Badge>
              {row.original.homeOrgDeleted && (
                <HomeOrgDeletedBadge
                  label={t('organization.members.homeOrgDeletedBadge')}
                  tooltip={t('organization.members.homeOrgDeletedTooltip')}
                />
              )}
            </div>
          );
        },
        enableColumnFilter: true
      },
      createActionsColumn<OrgMemberListItem>({
        onEdit: (member) => handleEdit(member),
        onDelete: (member) => handleDelete(member._id),
        onView: (member) => handleView(member),
        getCustomActions: (member) =>
          getMemberStatusActions(
            member,
            {
              onSendInvitation: handleSendInvitation,
              onSuspend: handleSuspend,
              onUnsuspend: handleUnsuspend
            },
            t
          ),
        isRowPending: (member) => pendingMemberIds.has(member._id)
      })
    ],
    [
      t,
      slug,
      isDepartmentManager,
      departmentsLoading,
      handleView,
      handleEdit,
      handleDelete,
      handleSendInvitation,
      handleSuspend,
      handleUnsuspend,
      deptLookup,
      pendingMemberIds,
      locationFilterOptions
    ]
  );

  return (
    <Page>
      <PageHeader>
        <OrganizationTabsNav />
      </PageHeader>
      <PageContent>
        <div className="flex items-center justify-between">
          <ContentHeader
            icon={ENTITY_ICONS.orgMember}
            title={t('organization.members.title')}
            description={t('organization.members.description')}
          />
          <div className="flex items-center gap-2">
            <InviteAllInactiveButton members={orgMembers ?? []} />
            <Button variant="primary" size="medium" onClick={handleCreate}>
              <Plus className="w-4 h-4" />
              {t('organization.members.createMember')}
            </Button>
          </div>
        </div>

        <div className="mt-8">
          <DataTable
            columns={columns}
            data={orgMembers || []}
            isLoading={isLoading}
            toolbarContent={<SeatUsageSummary className="ml-auto" />}
            emptyMessage={t('organization.members.empty')}
            enableGlobalFilter
            globalSearchPlaceholder={t('organization.members.search', {
              defaultValue: 'Search members...'
            })}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="orgMembers"
          />
        </div>

        {/* Form Dialog */}
        <OrgMemberFormDialog
          open={formDialogOpen}
          onOpenChange={(open) => !open && closeFormDialog()}
          onSubmit={handleFormSubmit}
          member={editingMember}
          {...memberDialogProps}
          showUserTypes={false}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={(open) => !open && closeDeleteDialog()}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
          entityPhrase={t('organization.members.deletePhrase')}
        />
      </PageContent>
    </Page>
  );
}
